/**
 * Een veldwerkopdracht (Excel, Word, PDF) omzetten naar leesbare tekst voor de AI.
 *
 * Het lastige zit in Excel. Veel formulieren zijn een raster van AANVINKVAKJES (formulier-
 * besturingselementen): "[x] Protocol 2001", "[ ] Protocol 2002". In de celwaarden staat dan
 * alleen het label; of het vakje aan staat, staat in een aparte tekening (xl/drawings/
 * vmlDrawingN.vml, <x:Checked>1). Zonder dat weet de AI niet welke protocollen, stoffen of
 * benodigdheden zijn aangevinkt -- en het hele formulier draait daarom.
 *
 * Verder:
 *   - datums staan als serienummer (45902) met een datumopmaak; die zetten we om (2025-09-03)
 *   - verborgen bladen (hulpbladen, keuzelijsten) laten we weg
 *   - foutwaarden en nullen uit lege formules (#DIV/0!, 0) laten we weg: ruis voor de AI
 *
 * Uitvoer per blad: "### Blad: <naam>", dan per regel "A2: Projectnummer | D2: 252349".
 */
import { unzipSync, strFromU8 } from 'fflate';
import { extractDocumentText } from '../extract-text.js';

const MAX_ROWS_PER_SHEET = 300;
const MAX_CHARS = 80_000;

const xmlDecode = (s) => String(s)
  .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'")
  .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
  .replace(/&#x([0-9a-f]+);/gi, (_, n) => String.fromCharCode(parseInt(n, 16)))
  .replace(/&amp;/g, '&');

export const colName = (i) => {
  let s = '';
  for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
};

const colIndex = (letters) => {
  let n = 0;
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
};

const NOISE = new Set(['0', '#DIV/0!', '#REF!', '#N/A', '#VALUE!', '#NAME?', '#NUM!', '#NULL!', '…..', '.....', '….', '…']);

/** Ingebouwde Excel-datumopmaken (14–22, 45–47) en eigen opmaak met d/m/j erin. */
function dateStyleSet(stylesXml) {
  const custom = new Map();
  for (const m of stylesXml.matchAll(/<numFmt\b[^>]*numFmtId="(\d+)"[^>]*formatCode="([^"]*)"/g)) custom.set(Number(m[1]), xmlDecode(m[2]));
  const isDateFmt = (id) => (id >= 14 && id <= 22) || (id >= 45 && id <= 47) ||
    (custom.has(id) && /[dmy]/i.test(custom.get(id).replace(/"[^"]*"|\[[^\]]*\]/g, '')) && !/^[#0.,%\s]+$/.test(custom.get(id)));
  const cellXfs = (stylesXml.match(/<cellXfs\b[^>]*>([\s\S]*?)<\/cellXfs>/) || [])[1] || '';
  const set = new Set();
  [...cellXfs.matchAll(/<xf\b([^>]*)\/?>/g)].forEach((m, i) => {
    const id = Number((m[1].match(/numFmtId="(\d+)"/) || [])[1] || 0);
    if (isDateFmt(id)) set.add(i);
  });
  return set;
}

/** Excel-serienummer → JJJJ-MM-DD (1900-systeem). */
export function excelSerialToDate(n) {
  const ms = Math.round((n - 25569) * 86400 * 1000);
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? String(n) : d.toISOString().slice(0, 10);
}

/** Pad relatief aan een .rels-bestand oplossen ("../drawings/vmlDrawing1.vml" vanuit xl/worksheets/). */
function resolvePath(fromDir, target) {
  if (target.startsWith('/')) return target.slice(1);
  const parts = fromDir.split('/').filter(Boolean);
  for (const seg of target.split('/')) {
    if (seg === '..') parts.pop();
    else if (seg !== '.') parts.push(seg);
  }
  return parts.join('/');
}

/**
 * Aanvinkvakjes van één blad: Map "rij,kolom" (0-gebaseerd) → true/false.
 * Een vakje staat in een cel; het label is de tekst in die cel (of de eerste ernaast).
 */
function checkboxesForSheet(files, sheetPath) {
  const out = new Map();
  const relsPath = sheetPath.replace(/worksheets\/(sheet\d+\.xml)$/, 'worksheets/_rels/$1.rels');
  const rels = files[relsPath] ? strFromU8(files[relsPath]) : '';
  for (const m of rels.matchAll(/<Relationship\b[^>]*Target="([^"]+\.vml)"/g)) {
    const vmlPath = resolvePath('xl/worksheets', m[1]);
    const vml = files[vmlPath] ? strFromU8(files[vmlPath]) : '';
    for (const sh of vml.matchAll(/<v:shape\b[\s\S]*?<\/v:shape>/g)) {
      if (!/ObjectType="Checkbox"/i.test(sh[0])) continue;
      const anchor = (sh[0].match(/<x:Anchor>\s*([^<]+)<\/x:Anchor>/) || [])[1];
      if (!anchor) continue;
      const [leftCol, , topRow] = anchor.split(',').map((s) => parseInt(s.trim(), 10));
      const checked = /<x:Checked>\s*1\s*<\/x:Checked>/.test(sh[0]);
      out.set(`${topRow},${leftCol}`, checked || out.get(`${topRow},${leftCol}`) === true);
    }
  }
  return out;
}

/**
 * Excel-werkmap → tekst. Zie de uitleg bovenaan.
 * @param {Uint8Array} bytes
 * @returns {{ text: string, sheets: { name: string, rows: number, checkboxes: number, checked: number }[] }}
 */
export function workbookToText(bytes) {
  const files = unzipSync(bytes, {
    filter: (f) => f.name.startsWith('xl/') && /\.(xml|rels|vml)$/.test(f.name),
  });
  const text = (name) => (files[name] ? strFromU8(files[name]) : '');
  const shared = [...text('xl/sharedStrings.xml').matchAll(/<si>([\s\S]*?)<\/si>/g)]
    .map((m) => xmlDecode([...m[1].matchAll(/<t[^>]*>([\s\S]*?)<\/t>/g)].map((t) => t[1]).join('')));
  const dateStyles = dateStyleSet(text('xl/styles.xml'));
  const target = {};
  for (const m of text('xl/_rels/workbook.xml.rels').matchAll(/<Relationship\b([^>]*)\/?>/g)) {
    const id = (m[1].match(/\bId="([^"]+)"/) || [])[1];
    const t = (m[1].match(/\bTarget="([^"]+)"/) || [])[1];
    if (id && t) target[id] = resolvePath('xl', t);
  }

  const blocks = [];
  const summary = [];
  for (const m of text('xl/workbook.xml').matchAll(/<sheet\b([^>]*)\/>/g)) {
    const name = xmlDecode((m[1].match(/\bname="([^"]*)"/) || [])[1] || '').trim();
    if (/\bstate="(hidden|veryHidden)"/.test(m[1])) continue;
    const rid = (m[1].match(/\br:id="([^"]+)"/) || [])[1];
    const path = target[rid];
    const xml = path ? text(path) : '';
    if (!xml) continue;

    // Cellen inlezen.
    const cells = new Map(); // rij → Map(kolom → tekst)
    for (const r of xml.matchAll(/<row\b[^>]*?\br="(\d+)"[^>]*>([\s\S]*?)<\/row>/g)) {
      const row = Number(r[1]) - 1;
      for (const c of r[2].matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
        const ref = (c[1].match(/\br="([A-Z]+)\d+"/) || [])[1];
        if (!ref) continue;
        const inner = c[2] || '';
        const type = (c[1].match(/\bt="([^"]+)"/) || [])[1];
        const style = Number((c[1].match(/\bs="(\d+)"/) || [])[1] || -1);
        const v = (inner.match(/<v>([\s\S]*?)<\/v>/) || [])[1];
        let val = '';
        if (type === 's' && v != null) val = shared[Number(v)] ?? '';
        else if (type === 'inlineStr') val = xmlDecode([...inner.matchAll(/<t[^>]*>([\s\S]*?)<\/t>/g)].map((t) => t[1]).join(''));
        else if (type === 'b') val = v === '1' ? 'ja' : 'nee';
        else if (v != null) {
          const n = Number(v);
          val = type !== 'str' && type !== 'e' && dateStyles.has(style) && n > 20000 && n < 80000
            ? excelSerialToDate(n)
            : xmlDecode(v);
        }
        val = String(val).replace(/\s+/g, ' ').trim();
        if (!val || NOISE.has(val)) continue;
        if (!cells.has(row)) cells.set(row, new Map());
        cells.get(row).set(colIndex(ref), val);
      }
    }

    // Aanvinkvakjes op hun label zetten.
    const boxes = checkboxesForSheet(files, path);
    let checked = 0;
    for (const [key, on] of boxes) {
      if (on) checked++;
      const [row, col] = key.split(',').map(Number);
      const rowCells = cells.get(row) || new Map();
      // Het label staat in de cel van het vakje, of anders in de eerste gevulde cel rechts ervan.
      let labelCol = rowCells.has(col) ? col : [...rowCells.keys()].filter((k) => k > col).sort((a, b) => a - b)[0];
      if (labelCol == null) { labelCol = col; rowCells.set(col, '(vakje zonder label)'); }
      const prev = rowCells.get(labelCol);
      if (!/^\[[x ]\] /.test(prev)) rowCells.set(labelCol, `${on ? '[x]' : '[ ]'} ${prev}`);
      cells.set(row, rowCells);
    }

    const rows = [...cells.keys()].sort((a, b) => a - b).slice(0, MAX_ROWS_PER_SHEET);
    if (!rows.length) continue;
    const lines = rows.map((r) => [...cells.get(r).entries()].sort((a, b) => a[0] - b[0])
      .map(([c, v]) => `${colName(c)}${r + 1}: ${v}`).join(' | '));
    blocks.push(`### Blad: ${name}${boxes.size ? ` (${checked} van ${boxes.size} vakjes aangevinkt)` : ''}\n${lines.join('\n')}`);
    summary.push({ name, rows: rows.length, checkboxes: boxes.size, checked });
  }
  let out = blocks.join('\n\n');
  if (out.length > MAX_CHARS) out = `${out.slice(0, MAX_CHARS)}\n[… afgekapt]`;
  return { text: out, sheets: summary };
}

/** Bestandstypen die als veldwerkopdracht gelezen kunnen worden. */
// Afbeeldingen (foto of scan van het formulier) kunnen alleen via 'tekst herkennen' (scan-ocr.js).
export const FIELD_ORDER_ACCEPT = '.xlsx,.xlsm,.pdf,.docx,.txt,.png,.jpg,.jpeg';

/**
 * Elk ondersteund bestand → tekst. Excel via workbookToText, de rest via de bestaande
 * documentlezer (PDF, Word, tekst).
 * @returns {Promise<{ text: string, kind: string, sheets?: object[] }>}
 */
export async function fieldOrderDocumentToText(file) {
  const ext = (file.name.toLowerCase().match(/\.([a-z0-9]+)$/) || [])[1] || '';
  if (ext === 'xls') throw new Error('Oud Excel-formaat (.xls). Sla het bestand op als .xlsx en probeer opnieuw.');
  if (ext === 'xlsx' || ext === 'xlsm') {
    const r = workbookToText(new Uint8Array(await file.arrayBuffer()));
    return { text: r.text, kind: 'excel', sheets: r.sheets };
  }
  if (['pdf', 'docx', 'txt'].includes(ext)) return { text: await extractDocumentText(file), kind: ext };
  // Een foto of scan heeft geen tekst; die gaat via 'tekst herkennen'.
  if (['png', 'jpg', 'jpeg'].includes(ext)) return { text: '', kind: 'afbeelding' };
  throw new Error('Dit bestandstype wordt niet ondersteund (Excel, PDF, Word of tekst).');
}
